import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { List, ListItem, Table } from "@mantine/core";

import { Fragment } from "react/jsx-runtime";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getNewbornVitalsRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";

interface NewbornVitalsRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  isViewOnly: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
}

export default function NewbornVitalsRow({
  time,
  observation,
  showNumbersOnly,
  isViewOnly,
  showPreviousVersions = true,
  onRefresh,
}: NewbornVitalsRowProps) {
  const {
    heartRate,
    respiratoryRate,
    temperature,
    color,
    void_,
    stool,
    stoolNotes,
    voidNotes,
    notes,
    hasAnswers,
    hasNotes,
  } = useMemo(() => getNewbornVitalsRowValues(observation), [observation]);

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  const versionCell = (
    <>
      <TextWrapper pt="xs" size="sm">
        Newborn Vitals:
      </TextWrapper>
      <List fz="sm" withPadding>
        {heartRate && (
          <ListItem>
            <strong>Heart Rate:</strong> {heartRate}
          </ListItem>
        )}
        {respiratoryRate && (
          <ListItem>
            <strong>Respiratory Rate:</strong> {respiratoryRate}
          </ListItem>
        )}
        {temperature && (
          <ListItem>
            <strong>Temperature:</strong> {temperature}
          </ListItem>
        )}
        {color && (
          <ListItem>
            <strong>Color:</strong> {color}
          </ListItem>
        )}
        {void_ && (
          <ListItem>
            <strong>Void:</strong> {void_}
          </ListItem>
        )}
        {stool && (
          <ListItem>
            <strong>Stool:</strong> {stool}
          </ListItem>
        )}
      </List>
    </>
  );

  const notesCell = (
    <>
      <TextWrapper pt="xs" size="sm">
        <strong>Stool Notes:</strong> {stoolNotes}
      </TextWrapper>
      <TextWrapper pt="xs" size="sm">
        <strong>Void Notes:</strong> {voidNotes}
      </TextWrapper>
      <TextWrapper pt="xs" size="sm">
        Newborn Vitals Comments: {notes}
      </TextWrapper>
    </>
  );

  const signatureCell =
    showPreviousVersions && previousVersions ? (
      <PreviousVersions
        observation={observation}
        rowComponent={NewbornVitalsRow}
        showPreviousVersions={showPreviousVersions}
      />
    ) : (
      <LastUpdated observation={observation} showPreviousVersions={false} />
    );

  const detailsRowNeeded = !showNumbersOnly && (hasAnswers || hasNotes);
  const mainRowRenders = hasAnswers && showPreviousVersions;

  if (!hasAnswers && !hasNotes) return null;

  return (
    <Fragment>
      {mainRowRenders && (
        <Table.Tr bg="white">
          <PostpartumTimeCell
            time={time}
            onRefresh={onRefresh}
            isViewOnly={isViewOnly}
            observation={observation}
            rowSpan={detailsRowNeeded ? 2 : 1}
          />
          <Table.Td>{heartRate}</Table.Td>
          <Table.Td>{respiratoryRate}</Table.Td>
          <Table.Td>{temperature}</Table.Td>
          <Table.Td>{color}</Table.Td>
          <Table.Td>{void_}</Table.Td>
          <Table.Td>{stool}</Table.Td>
        </Table.Tr>
      )}
      {detailsRowNeeded && (
        <Table.Tr bg="white" className={classes.tableRow}>
          {!mainRowRenders && showPreviousVersions && (
            <PostpartumTimeCell
              time={time}
              rowSpan={1}
              onRefresh={onRefresh}
              isViewOnly={isViewOnly}
              observation={observation}
            />
          )}
          <Table.Td p="md" colSpan={6}>
            {!showPreviousVersions && versionCell}
            {hasNotes && notesCell}
            {signatureCell}
          </Table.Td>
        </Table.Tr>
      )}
    </Fragment>
  );
}
